import { useCallback, useEffect, useRef, useState } from "react";
import { Grid3X3, Download, Plus, X, Save, ChevronLeft, ChevronRight, ScanLine, MapPin, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { beep } from "@/lib/sound";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inputCls = "w-full bg-white border border-[#D9E3DC] rounded-md px-3 py-2 text-sm text-[#0F2A1F] focus:outline-none focus:ring-2 focus:ring-[#0F6B3A]";

function CelulaEditor({ cel, tipo, mapa, onClose, onSaved }) {
  const [codes, setCodes] = useState(cel.itens.map((i) => i.codigo));
  const [novo, setNovo] = useState("");
  const [saving, setSaving] = useState(false);
  const add = () => {
    const parts = novo.split(/[;,\s]+/).map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    setCodes((c) => [...c, ...parts.filter((p) => !c.includes(p))]);
    setNovo("");
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/enderecos/celula", { tipo, estacao: mapa.estacao, rack: mapa.rack, linha: cel.linha, coluna: cel.coluna, codigos: codes });
      toast.success(`${data.endereco}: +${data.adicionados.length} / −${data.removidos.length}`);
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao salvar célula"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="celula-editor">
        <DialogHeader>
          <DialogTitle className="font-mono text-2xl text-[#0F2A1F]">{mapa.estacao} {mapa.rack} {cel.linha}{cel.coluna}</DialogTitle>
          <DialogDescription className="text-xs text-[#5A6B62]">Adicione ou remova os códigos desta posição. Vários códigos: separe com ;</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2 min-h-[40px]" data-testid="celula-codigos">
            {codes.length === 0 && <span className="text-xs text-[#8A9A90] italic">Posição vazia</span>}
            {codes.map((c) => (
              <span key={c} className="inline-flex items-center gap-1 bg-[#EAF7EF] border border-[#B7E4C7] text-[#0F6B3A] font-mono font-bold text-sm px-2.5 py-1.5 rounded-md" data-testid={`celula-chip-${c}`}>
                {c}<button onClick={() => setCodes(codes.filter((x) => x !== c))} data-testid={`celula-remove-${c}`} className="text-[#0F6B3A]/70 hover:text-[#B91C1C]"><X className="w-3.5 h-3.5" /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input autoFocus data-testid="celula-novo-input" value={novo} onChange={(e) => setNovo(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
              placeholder="Código do produto" inputMode="numeric" className={`${inputCls} h-12 text-lg font-mono`} />
            <button data-testid="celula-add-btn" onClick={add} className="h-12 px-4 bg-[#0F6B3A] hover:bg-[#0B5530] text-white rounded-md font-bold flex items-center gap-1"><Plus className="w-4 h-4" /></button>
          </div>
          <button data-testid="celula-save-btn" onClick={save} disabled={saving} className="w-full h-12 bg-[#2DC26B] hover:bg-[#22A85B] disabled:opacity-60 text-white font-bold rounded-md flex items-center justify-center gap-2"><Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar posição"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function MapaEnderecos({ tipo, onChanged }) {
  const [mapa, setMapa] = useState(null);
  const [estacao, setEstacao] = useState("");
  const [rack, setRack] = useState("");
  const [cel, setCel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scan, setScan] = useState("");
  const [scanRes, setScanRes] = useState(null);
  const [highlight, setHighlight] = useState(null);
  const scanRef = useRef(null);
  const pendingHighlight = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setMapa((await api.get("/enderecos/mapa", { params: { tipo, estacao: estacao || undefined, rack: rack || undefined } })).data); }
    catch { toast.error("Erro ao carregar mapa"); } finally { setLoading(false); }
  }, [tipo, estacao, rack]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setEstacao(""); setRack(""); setScanRes(null); setHighlight(null); }, [tipo]);

  const scrollToCell = (key) => {
    const [l, c] = key.split("|");
    const el = document.querySelector(`[data-testid="mapa-cel-${l}${c}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  useEffect(() => {
    if (!mapa || !pendingHighlight.current) return;
    const key = pendingHighlight.current; pendingHighlight.current = null;
    setTimeout(() => scrollToCell(key), 120);
  }, [mapa]); // eslint-disable-line react-hooks/exhaustive-deps

  const racks = mapa?.racks || [];
  const idx = racks.indexOf(mapa?.rack);
  const goRack = (d) => { const r = racks[idx + d]; if (r != null) setRack(String(r)); };

  const localizar = async () => {
    const code = scan.trim();
    if (!code) return;
    try {
      const { data } = await api.get("/enderecos/localizar", { params: { tipo, codigo: code } });
      setScanRes(data);
      if (data.encontrado) {
        beep("scan-ok");
        const loc = data.localizacoes[0];
        const key = `${loc.linha}|${loc.coluna}`;
        setHighlight(key);
        const mudou = String(loc.estacao) !== String(mapa?.estacao) || Number(loc.rack) !== Number(mapa?.rack);
        if (mudou) { pendingHighlight.current = key; setEstacao(String(loc.estacao)); setRack(String(loc.rack)); }
        else scrollToCell(key);
      } else { beep("alert"); setHighlight(null); }
    } catch { toast.error("Erro na busca"); }
    setScan(""); scanRef.current?.focus();
  };

  const criar = async (kind) => {
    const v = window.prompt(kind === "linha" ? "Número da nova linha (ex.: 5)" : "Letra da nova coluna (ex.: V)");
    if (!v) return;
    try { const { data } = await api.post("/enderecos/mapa/celulas", { tipo, estacao: mapa.estacao, rack: mapa.rack, [kind]: v }); toast.success(`${data.criados} posição(ões) criada(s)`); load(); onChanged?.(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erro ao criar"); }
  };
  const exportar = async () => {
    try {
      const res = await api.get("/enderecos/export", { params: { tipo }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${tipo === "separacao" ? "Separacao" : "Pulmao"}.xlsx`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Erro ao exportar"); }
  };

  const baseLabel = tipo === "separacao" ? "Separação" : "Pulmão";

  return (
    <div className="space-y-3" data-testid="mapa-enderecos">
      {/* Bipagem de verificação */}
      <div className="bg-[#0B1F17] rounded-lg shadow-sm p-3" data-testid="mapa-scanner">
        <div className="flex items-center gap-1.5 text-[#2DC26B] text-[10px] font-bold tracking-widest uppercase mb-2"><ScanLine className="w-3.5 h-3.5" /> Bipar produto · base {baseLabel}</div>
        <div className="flex gap-2">
          <input ref={scanRef} data-testid="mapa-scan-input" value={scan} onChange={(e) => setScan(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); localizar(); } }}
            inputMode="numeric" autoFocus placeholder="Bipe ou digite o código e tecle Enter"
            className="flex-1 h-12 bg-white border-2 border-[#2DC26B] rounded-md px-3 text-lg font-mono font-bold text-[#0F2A1F] focus:outline-none focus:ring-2 focus:ring-[#2DC26B]" />
          <button data-testid="mapa-scan-btn" onClick={localizar} className="h-12 px-5 bg-[#2DC26B] hover:bg-[#22A85B] text-white font-bold rounded-md flex items-center gap-2"><ScanLine className="w-4 h-4" /> Localizar</button>
        </div>
        {scanRes && (
          scanRes.encontrado ? (
            <div className="mt-2.5 bg-[#0F2A1F] border border-[#2DC26B] rounded-md p-2.5" data-testid="mapa-scan-result">
              <div className="flex items-center gap-2 text-[#2DC26B] font-bold text-sm">
                <CheckCircle2 className="w-4 h-4" /> {scanRes.codigo}
                {scanRes.ean && (
                  <span className="text-[10px] font-mono font-normal text-slate-300 bg-white/10 border border-[#2DC26B]/40 px-1.5 py-0.5 rounded" data-testid="mapa-scan-ean-tag">EAN {scanRes.ean}</span>
                )}
              </div>
              <div className="text-slate-300 text-xs truncate">{scanRes.nome || "—"}</div>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {scanRes.localizacoes.map((l) => (
                  <button key={l.endereco} data-testid={`mapa-scan-loc-${l.endereco.replace(/\s+/g, "")}`}
                    onClick={() => { const key = `${l.linha}|${l.coluna}`; setHighlight(key); const mudou = String(l.estacao) !== String(mapa?.estacao) || Number(l.rack) !== Number(mapa?.rack); if (mudou) { pendingHighlight.current = key; setEstacao(String(l.estacao)); setRack(String(l.rack)); } else scrollToCell(key); }}
                    className="inline-flex items-center gap-1 bg-white/10 hover:bg-white/20 border border-[#2DC26B]/50 text-white font-mono font-bold text-sm px-2.5 py-1.5 rounded-md">
                    <MapPin className="w-3.5 h-3.5 text-[#2DC26B]" /> {l.endereco}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-2.5 bg-[#3B0D0D] border border-[#EF4444] rounded-md p-2.5 flex items-center gap-2 text-[#FCA5A5] font-semibold text-sm" data-testid="mapa-scan-notfound">
              <AlertTriangle className="w-4 h-4" /> Código {scanRes.codigo} não encontrado na base {baseLabel}.
            </div>
          )
        )}
      </div>

      <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-3 grid grid-cols-2 md:grid-cols-12 gap-2 items-center">
        <select data-testid="mapa-estacao-select" value={mapa?.estacao || ""} onChange={(e) => { setEstacao(e.target.value); setRack(""); }} className={`${inputCls} md:col-span-3`}>
          {(mapa?.estacoes || []).map((e) => <option key={e} value={e}>Estação {e}</option>)}
        </select>
        <div className="flex items-center gap-1 md:col-span-4">
          <button data-testid="mapa-rack-prev" onClick={() => goRack(-1)} disabled={idx <= 0} className="h-10 w-10 shrink-0 border border-[#D9E3DC] rounded-md flex items-center justify-center disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
          <select data-testid="mapa-rack-select" value={mapa?.rack ?? ""} onChange={(e) => setRack(e.target.value)} className={inputCls}>
            {racks.map((r) => <option key={r} value={r}>Rack {r}</option>)}
          </select>
          <button data-testid="mapa-rack-next" onClick={() => goRack(1)} disabled={idx < 0 || idx >= racks.length - 1} className="h-10 w-10 shrink-0 border border-[#D9E3DC] rounded-md flex items-center justify-center disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="col-span-2 md:col-span-5 flex flex-wrap gap-2 md:justify-end text-xs">
          <button data-testid="mapa-criar-linha" onClick={() => criar("linha")} className="h-10 px-3 border border-[#D9E3DC] rounded-md font-semibold text-[#0F2A1F] hover:bg-[#F1F5F2] flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Linha</button>
          <button data-testid="mapa-criar-coluna" onClick={() => criar("coluna")} className="h-10 px-3 border border-[#D9E3DC] rounded-md font-semibold text-[#0F2A1F] hover:bg-[#F1F5F2] flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Coluna</button>
          <button data-testid="mapa-exportar" onClick={exportar} className="h-10 px-3 bg-[#0F6B3A] hover:bg-[#0B5530] text-white rounded-md font-semibold flex items-center gap-1"><Download className="w-3.5 h-3.5" /> Exportar {baseLabel}</button>
        </div>
      </div>
      {mapa && (
        <div className="font-mono text-[11px] text-[#8A9A90] flex flex-wrap gap-x-4" data-testid="mapa-info">
          <span><Grid3X3 className="w-3 h-3 inline mr-1" />{mapa.linhas.length} linhas × {mapa.colunas.length} colunas</span>
          <span>{mapa.total_codigos} códigos</span>
          {mapa.area && <span>Área linha separação: {mapa.area}</span>}
          <span className="text-[#5A6B62]">Toque em uma célula para alterar/apagar códigos</span>
        </div>
      )}
      <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm overflow-auto max-h-[70vh]">
        {loading && !mapa ? <div className="py-10 text-center text-[#8A9A90]">Carregando...</div> : !mapa || mapa.rack == null ? <div className="py-10 text-center text-[#8A9A90]" data-testid="mapa-empty">Nenhum rack nesta estação</div> : (
          <table className="border-collapse text-sm" data-testid="mapa-grid">
            <thead className="sticky top-0 z-10">
              <tr>
                <th className="sticky left-0 z-20 bg-[#0B1F17] text-white font-mono text-xs px-3 py-2 text-left">Rack {mapa.rack}</th>
                {mapa.colunas.map((c) => <th key={c} className="bg-[#F4F8F5] border-b border-l border-[#D9E3DC] font-mono font-bold text-[#0F2A1F] px-2 py-2 min-w-[92px]">{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {mapa.linhas.map((l) => (
                <tr key={l}>
                  <th className="sticky left-0 z-10 bg-[#F4F8F5] border-r border-b border-[#D9E3DC] font-mono font-bold text-[#0F2A1F] px-3 py-2 text-left">{l}</th>
                  {mapa.colunas.map((c) => {
                    const itens = mapa.celulas[`${l}|${c}`] || [];
                    const vazio = itens.length === 0;
                    const isHi = highlight === `${l}|${c}`;
                    return (
                      <td key={c} className="border-b border-l border-[#D9E3DC] p-0 align-top">
                        <button data-testid={`mapa-cel-${l}${c}`} onClick={() => setCel({ linha: l, coluna: c, itens })}
                          className={`w-full h-full min-h-[56px] px-2 py-1.5 text-left transition-colors ${isHi ? "bg-[#FFF7DA] ring-4 ring-[#F2B01E] ring-inset" : vazio ? "hover:bg-[#F4F8F5]" : "bg-[#EAF7EF]/60 hover:bg-[#D3F2DF]"}`}>
                          {vazio ? <span className="text-[10px] text-[#8A9A90] font-mono">—</span> : itens.map((i) => (
                            <div key={i.codigo} className="leading-tight">
                              <span className={`font-mono font-bold text-[13px] ${i.disponivel <= 0 ? "text-[#B91C1C]" : "text-[#0F6B3A]"}`}>{i.codigo}</span>
                              <div className="text-[9px] text-[#5A6B62] truncate max-w-[110px]" title={i.nome}>{i.nome}</div>
                            </div>
                          ))}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {cel && <CelulaEditor cel={cel} tipo={tipo} mapa={mapa} onClose={() => setCel(null)} onSaved={() => { setCel(null); load(); onChanged?.(); }} />}
    </div>
  );
}
